import { keepPreviousData, useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { formatRelative } from '~/utils/date';
import { extractApiError } from '~/utils/extract-api-error';

import { listExports } from '../../services/export.service';
import { useExportStore } from '../../store/export.store';
import type { ExportConnector, ExportTriggerResponse } from '../../types/export.types';
import {
  browseCatalog,
  estimateCatalogExport,
  getCatalogBatches,
  getCatalogFacets,
  getCatalogSummary,
  getCatalogVersions,
  triggerCatalogExport,
} from '../services/catalog-export.service';
import type {
  CatalogBatchPreset,
  CatalogBrowseRequest,
  CatalogExportRequest,
  CatalogFacetsRequest,
  CatalogTriggerRequest,
  CatalogVersionsRequest,
} from '../types/catalog-export.types';
import { catalogSourceId } from '../types/catalog-export.types';

/** The batch picker. The server caches it 60 s per tenant + connector, so we match that. */
export function useCatalogBatches(
  connector: ExportConnector,
  preset: CatalogBatchPreset,
  includeArchived: boolean,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.catalogExports.batches(connector, preset, includeArchived),
    queryFn: () => getCatalogBatches(connector, preset, includeArchived),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * The metrics bar. Keyed on the whole request: every catalog and filter field
 * moves the numbers. The previous numbers stay on screen while a change
 * re-reads (`keepPreviousData`), so the bar never collapses to skeletons.
 */
export function useCatalogSummary(request: CatalogExportRequest, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogExports.summary(request),
    queryFn: () => getCatalogSummary(request),
    enabled,
    // Numbers only move when a batch lands; the server caches 5 min too.
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * One keyset page of the catalog grid. The previous page stays on screen while
 * the next loads (`placeholderData`), as on the batch browse page. Catalog
 * numbers move only when a batch lands, so a page is fresh for 5 minutes.
 */
export function useCatalogBrowse(request: CatalogBrowseRequest, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogExports.browse(request),
    queryFn: () => browseCatalog(request),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previous) => previous,
  });
}

/**
 * Dropdown values for the grid's column filters. An environment whose export
 * service predates the route answers 404/405; the query then resolves to `null`
 * and the grid keeps free-text filters, rather than erroring.
 */
export function useCatalogFacets(request: CatalogFacetsRequest, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogExports.facets(request),
    queryFn: async () => {
      try {
        return await getCatalogFacets(request);
      } catch (err) {
        if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 405)) {
          return null;
        }
        throw err;
      }
    },
    enabled: enabled && request.keys.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** The API's cap on keys per facets request. */
const FACET_KEYS_PER_REQUEST = 10;

/**
 * Dropdown values for any number of keys (the filters dialog), fetched in
 * parallel requests of at most 10 keys and merged. Same fallback as
 * `useCatalogFacets`: a service without the route yields no values, not an error.
 */
export function useCatalogFacetsMany(
  request: Omit<CatalogFacetsRequest, 'keys'>,
  keys: readonly string[],
  enabled: boolean,
) {
  const chunks: string[][] = [];
  for (let i = 0; i < keys.length; i += FACET_KEYS_PER_REQUEST) {
    chunks.push(keys.slice(i, i + FACET_KEYS_PER_REQUEST));
  }
  return useQueries({
    queries: chunks.map((chunk) => {
      const body: CatalogFacetsRequest = { ...request, keys: chunk };
      return {
        queryKey: queryKeys.catalogExports.facets(body),
        queryFn: async () => {
          try {
            return await getCatalogFacets(body);
          } catch (err) {
            if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 405)) {
              return null;
            }
            throw err;
          }
        },
        enabled,
        // A drafted filter changes every chunk's key: keep the last counts on screen meanwhile.
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false,
      };
    }),
    combine: (results) => ({
      facets: Object.assign({}, ...results.map((r) => r.data?.facets ?? {})) as Record<
        string,
        { values: { value: string; count: number }[]; truncated: boolean }
      >,
      isPending: results.some((r) => r.isPending && r.fetchStatus !== 'idle'),
    }),
  });
}

/** Every version of one SKU or product, for the row sheet's Versions tab. */
export function useCatalogVersions(request: CatalogVersionsRequest, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogExports.versions(request),
    queryFn: () => getCatalogVersions(request),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Completed exports the throughput is measured over — enough to smooth one outlier. */
const THROUGHPUT_SAMPLE = 20;

/**
 * Rows per second this subsidiary's exports actually achieved, from its own
 * completed history (written rows ÷ wall time, over the last 20 that wrote
 * anything). `null` when there is no history to measure — the bar then shows no
 * time rather than a made-up one.
 */
export function useExportThroughput() {
  return useQuery({
    queryKey: queryKeys.catalogExports.throughput(),
    queryFn: async () => {
      const done = (await listExports({ status: 'COMPLETED', limit: 100 }))
        .filter((e) => e.writtenProducts > 0 && e.createdAt && e.completedAt)
        .slice(0, THROUGHPUT_SAMPLE);
      let rows = 0;
      let seconds = 0;
      for (const e of done) {
        const span = (Date.parse(e.completedAt ?? '') - Date.parse(e.createdAt ?? '')) / 1000;
        if (!Number.isFinite(span) || span <= 0) continue;
        rows += e.writtenProducts;
        seconds += span;
      }
      return seconds > 0 ? { rowsPerSecond: rows / seconds, sample: done.length } : null;
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** Dry-run row count + size for the export bar. A mutation, like the batch estimate. */
export function useEstimateCatalogExport() {
  return useMutation({
    mutationFn: (request: CatalogExportRequest) => estimateCatalogExport(request),
  });
}

interface TriggerCatalogVars {
  request: CatalogTriggerRequest;
  /** Human context for the progress toast. */
  label: string;
}

/** `existingWorkflowId=…` from a 409's `errors[]` — the export already running. */
function existingWorkflowId(err: ApiError): string | null {
  const body = err.body as { errors?: unknown } | undefined;
  const errors = Array.isArray(body?.errors) ? body.errors : [];
  for (const entry of errors) {
    if (typeof entry === 'string' && entry.startsWith('existingWorkflowId=')) {
      return entry.slice('existingWorkflowId='.length);
    }
  }
  return null;
}

/**
 * Start a catalog export and hand it to the app-level progress manager — the
 * same toast, SSE stream and download as a batch export.
 *
 * A 409 (an identical catalog export is running) adopts the running one, but
 * only when no template was asked for: the server's duplicate guard keys the
 * selection, filters, format and packaging, NOT the template or columns, so a
 * feed-template request can collide with a raw one. Adopting that would hand
 * the user a file that is not what they picked — the batch export's rule.
 */
export function useTriggerCatalogExport() {
  const startExport = useExportStore((s) => s.startExport);
  const { t } = useTranslation('catalog-export');

  return useMutation({
    mutationFn: async ({ request }: TriggerCatalogVars): Promise<ExportTriggerResponse> => {
      try {
        return await triggerCatalogExport(request);
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.statusCode === 409 &&
          !request.template &&
          !request.fields
        ) {
          const workflowId = existingWorkflowId(err);
          const running = (
            await listExports({ sourceWorkflowId: catalogSourceId(request.catalog.connector) })
          ).find(
            (e) =>
              (e.status === 'PENDING' || e.status === 'IN_PROGRESS') &&
              (workflowId === null || e.workflowId === workflowId),
          );
          if (running) {
            return {
              exportId: running.exportId,
              workflowId: running.workflowId,
              runId: '',
              status: 'IN_PROGRESS',
            };
          }
        }
        throw err;
      }
    },
    onSuccess: (res, { request, label }) => {
      startExport({
        exportId: res.exportId,
        workflowId: res.workflowId,
        sourceWorkflowId: catalogSourceId(request.catalog.connector),
        label,
        connector: request.catalog.connector,
        tenantContext: request.tenantContext,
        catalogRequest: request,
        toastId: `export-${res.exportId}`,
        latest: null,
        startedAt: Date.now(),
      });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.statusCode === 409) {
        // The trigger's other 409: the GMC pull is older than "Only products live in GMC" allows.
        const body = err.body as { lastSyncedAt?: unknown } | undefined;
        if (body && 'lastSyncedAt' in body) {
          toast.error(
            typeof body.lastSyncedAt === 'string'
              ? t('toast.staleLiveness', { when: formatRelative(body.lastSyncedAt) })
              : t('toast.noLivenessPull'),
          );
          return;
        }
        toast.error(t('toast.conflict'));
        return;
      }
      toast.error(extractApiError(err, t('toast.triggerError')));
    },
  });
}
